import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, Clock, Stethoscope, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal";

function todayStr() { return new Date().toISOString().slice(0, 10); }

export default function BookingWizard() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [hospital, setHospital] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [step, setStep] = useState(1);

  const [doctorId, setDoctorId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [slotInfo, setSlotInfo] = useState({ slots: [], active: true });
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);

  const [patient, setPatient] = useState({ name: "", contact: "", email: "", reason: "" });
  const [consent, setConsent] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  // Load hospital + doctors
  useEffect(() => {
    (async () => {
      try {
        const [h, d] = await Promise.all([
          api.get(`/public/hospitals/${slug}`),
          api.get(`/public/hospitals/${slug}/doctors`),
        ]);
        setHospital(h.data);
        setDoctors(d.data || []);
        if ((d.data || []).length === 1) setDoctorId(d.data[0].id);
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    })();
  }, [slug]);

  // Load slots when doctor + date set
  useEffect(() => {
    if (!doctorId || !date) { setSlotInfo({ slots: [], active: true }); return; }
    (async () => {
      try {
        const { data } = await api.get(`/public/hospitals/${slug}/doctors/${doctorId}/slots`, { params: { date } });
        setSlotInfo(data || { slots: [], active: true });
        setSlot("");
      } catch (e) {
        toast.error(formatErr(e?.response?.data?.detail || e));
      }
    })();
  }, [doctorId, date, slug]);

  const selectedDoctor = useMemo(() => doctors.find((d) => d.id === doctorId), [doctorId, doctors]);

  const next = () => {
    if (step === 1 && !doctorId) return toast.error("Select a doctor");
    if (step === 2 && (!date || !slot)) return toast.error("Pick a date and time");
    if (step === 3) {
      if (!patient.name.trim() || !patient.contact.trim()) return toast.error("Name and contact are required");
      if (!consent) return toast.error("Please read and agree to the Privacy Policy to continue");
      return submit();
    }
    setStep(step + 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/reservations", {
        hospital_id: slug,
        doctor_id: doctorId,
        reservation_date: date,
        reservation_time: slot,
        patient_name: patient.name,
        patient_contact: patient.contact,
        patient_email: patient.email,
        reason: patient.reason,
      });
      const rid = data?.reservation?.id;
      if (!rid) throw new Error("No reservation id returned");
      if (data?.payment_required === false) {
        // No payment step — straight to confirmation
        nav(`/book/${slug}/confirmed/${rid}`, { replace: true });
      } else {
        nav(`/book/${slug}/payment/${rid}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  if (!hospital) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-wizard">
      <header className="bg-gradient-to-r from-blue-800 to-cyan-500 text-white">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Link to="/book" className="inline-flex items-center gap-1 text-white/85 hover:text-white text-sm"><ArrowLeft size={14} />All clinics</Link>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ fontFamily: "Work Sans" }}>{hospital.hospital_name}</h1>
          <p className="text-white/80 text-sm mt-1">{hospital.address}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 text-xs font-medium">
          {["Doctor", "Date & Time", "Patient"].map((label, i) => {
            const n = i + 1;
            return (
              <div key={label} className="flex items-center gap-2">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center ${step >= n ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {step > n ? <CheckCircle2 size={14} /> : n}
                </div>
                <span className={step >= n ? "text-slate-900" : "text-slate-400"}>{label}</span>
                {i < 2 ? <div className="w-6 h-px bg-slate-300" /> : null}
              </div>
            );
          })}
        </div>

        {/* Step 1: doctor */}
        {step === 1 && (
          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="step-doctor">
            <h2 className="font-bold text-slate-900">Select a doctor</h2>
            {doctors.length === 0 ? (
              <div className="text-slate-500 text-sm">No doctors currently accept online appointments.</div>
            ) : doctors.map((d) => (
              <label key={d.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${doctorId === d.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="doctor" value={d.id} checked={doctorId === d.id} onChange={() => setDoctorId(d.id)} className="sr-only" />
                <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><Stethoscope size={18} /></div>
                <div>
                  <div className="font-semibold text-slate-900">{d.name}</div>
                  <div className="text-xs text-slate-500">{d.specialty || "Clinic Doctor"}</div>
                </div>
              </label>
            ))}
          </section>
        )}

        {/* Step 2: date & time */}
        {step === 2 && (
          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="step-slot">
            <h2 className="font-bold text-slate-900">Pick a date & time</h2>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <Stethoscope size={16} className="text-blue-700" />
              <span>{selectedDoctor?.name} · {selectedDoctor?.specialty || "Clinic Doctor"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</label>
                <div className="mt-1 relative">
                  <CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="h-11 pl-9" data-testid="slot-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Available Times</label>
                {!slotInfo.active ? (
                  <div className="mt-2 text-sm text-red-600">Doctor is not available on this date.</div>
                ) : slotInfo.slots.length === 0 ? (
                  <div className="mt-2 text-sm text-slate-500">No open slots for this date.</div>
                ) : (
                  <div className="mt-1 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-auto pr-1">
                    {slotInfo.slots.map((s) => (
                      <button key={s} type="button" onClick={() => setSlot(s)}
                        className={`h-10 rounded-lg border text-sm font-medium ${slot === s ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 hover:border-blue-400 text-slate-700"}`}
                        data-testid={`slot-${s}`}
                      >
                        <Clock size={12} className="inline mr-1 -mt-0.5" />{s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Step 3: patient */}
        {step === 3 && (
          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="step-patient">
            <h2 className="font-bold text-slate-900">Your details</h2>
            <Input placeholder="Full name *" value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className="h-11" data-testid="patient-name" />
            <Input placeholder="Contact number *" value={patient.contact} onChange={(e) => setPatient({ ...patient, contact: e.target.value })} className="h-11" data-testid="patient-contact" />
            <Input placeholder="Email (optional)" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} className="h-11" />
            <Input placeholder="Reason for visit (optional)" value={patient.reason} onChange={(e) => setPatient({ ...patient, reason: e.target.value })} className="h-11" />
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              After submitting, you will scan the clinic’s payment QR code and upload your receipt. Your slot is held only after staff verifies the payment.
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 pt-1 cursor-pointer" data-testid="privacy-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>
                I have read and agree to the{" "}
                <button type="button" onClick={(e) => { e.preventDefault(); setPolicyOpen(true); }} className="text-blue-700 font-medium underline">Privacy Policy</button>
                {" "}and consent to the processing of my personal and health information.
              </span>
            </label>
          </section>
        )}

        {/* Footer */}
        <div className="mt-5 flex justify-between gap-3">
          <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="h-12 px-6">Back</Button>
          <Button onClick={next} disabled={busy} data-testid="booking-next" className="h-12 flex-1 sm:flex-none sm:min-w-[160px] bg-blue-700 hover:bg-blue-800">
            {step === 3 ? (busy ? "Submitting…" : "Submit & Pay") : "Continue"}
          </Button>
        </div>
      </main>

      <PrivacyPolicyModal open={policyOpen} onOpenChange={setPolicyOpen} clinicName={hospital?.hospital_name} />
    </div>
  );
}
